/**
 * Sales and purchase registers.
 *
 * Asked for by name after the first demo. The day book already listed
 * sales vouchers, but a register is a different document: one row per
 * invoice, split into taxable value, VAT and total, in columns that add
 * up. It is what gets handed over at VAT filing time, which is why the
 * taxable and VAT columns are totalled separately.
 *
 * One screen for both, because they are the same report read from the two
 * sides of the same transaction, and a clerk who learns one has learned
 * the other.
 *
 * Reversals appear as negative rows. They are not hidden and they are not
 * shown as positives: both halves of a correction are on the VAT return,
 * and a register that quietly dropped one would not reconcile with the
 * ledger it is drawn from.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box, Chip, Skeleton, Stack, Tab, Tabs, Typography, useMediaQuery,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { fetchRegister } from '../api/endpoints'
import type { RegisterRow } from '../api/types'
import { c, fmtMoney } from '../theme'
import ReportShell, { rangeLabel } from './ReportShell'
import ReportFooter from './ReportFooter'
import ReportToolbar from './ReportToolbar'
import VoucherDrawer from './VoucherDrawer'
import { useDateRange } from './useDateRange'
import { downloadCsv } from './csv'
import { gregorian } from './hijri'

type Kind = 'sales' | 'purchase'

/** A negative row is a reversal or a credit note. It must not be
 *  mistaken for revenue at a glance. */
const amountColor = (v: string) => (Number(v) < 0 ? c.dangerInk : undefined)

export default function Register({ kind: initial = 'sales' }:
{ kind?: Kind }) {
  const { t, i18n } = useTranslation()
  const { range, setRange, presets } = useDateRange()
  const [kind, setKind] = useState<Kind>(initial)
  const [open, setOpen] = useState<number | null>(null)
  const narrow = useMediaQuery('(max-width:700px)')

  const q = useQuery({
    queryKey: ['register', kind, range.from, range.to],
    queryFn: () => fetchRegister({
      kind, from_date: range.from, to_date: range.to }),
  })

  const rows = q.data?.rows ?? []

  return (
    <>
      <ReportShell
        titleKey={kind === 'sales' ? 'nav.salesRegister' : 'nav.purchaseRegister'}
        period={rangeLabel(range.from, range.to, i18n.language, t)}
        headRight={q.data
          ? <Chip size="small" variant="outlined" label={q.data.count} />
          : undefined}
        toolbar={
          <ReportToolbar range={range} setRange={setRange} presets={presets}
            pdf={{ path: 'register.pdf',
                   params: { kind, from_date: range.from, to_date: range.to },
                   filename: `${kind}-register-${range.from}-${range.to}.pdf` }}
            onExport={() => downloadCsv(
              `${kind}-register-${range.from}-${range.to}`, [
                ['Date', 'Voucher', 'Party', 'VAT no.', 'Taxable', 'VAT',
                 'Total', 'Status'],
                ...rows.map((r) => [
                  r.date, r.voucher_no, r.party_name ?? '',
                  r.party_tax_number ?? '', r.taxable, r.vat, r.total,
                  r.status]),
              ])}>
            {/* Tabs rather than a dropdown: there are exactly two, and
                which one you are looking at should never be a question. */}
            <Tabs value={kind} onChange={(_, v) => setKind(v as Kind)}
              sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}>
              <Tab value="sales" label={t('report.sales')} />
              <Tab value="purchase" label={t('report.purchase')} />
            </Tabs>
          </ReportToolbar>
        }>

        {q.isLoading ? (
          <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
            {t('report.nothingInRange')}
          </Typography>
        ) : narrow ? (
          /* On a phone the seven columns become a card per invoice, the
             same treatment the outstanding report gets. A table this
             wide either scrolls sideways or shrinks past reading. */
          <Box className="ruled">
            {rows.map((r) => (
              <RegisterCard key={r.voucher_id} row={r}
                onOpen={() => setOpen(r.voucher_id)} />
            ))}
            <Stack direction="row" justifyContent="space-between"
              sx={{ px: 2, py: 1.5, bgcolor: c.surfacePage }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                color: c.inkMuted }}>
                {t('report.total')}
              </Typography>
              <span className="num" style={{ fontWeight: 600 }}>
                {fmtMoney(q.data!.total)}
              </span>
            </Stack>
          </Box>
        ) : (
          <Box sx={{ px: 2, pb: 1 }}>
            <table className="rpt">
              <thead>
                <tr>
                  <th style={{ width: 104 }}>{t('entry.date')}</th>
                  <th style={{ width: 142 }}>{t('report.voucherNo')}</th>
                  <th>{t('report.party')}</th>
                  <th style={{ width: 132 }}>{t('chart.vatNumber')}</th>
                  <th className="num" style={{ width: 118 }}>
                    {t('report.taxable')}</th>
                  <th className="num" style={{ width: 104 }}>
                    {t('report.vat')}</th>
                  <th className="num" style={{ width: 124 }}>
                    {t('report.total')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.voucher_id} className="clickable"
                    onClick={() => setOpen(r.voucher_id)}>
                    <td className="num" style={{ textAlign: 'start' }}>
                      {gregorian(r.date, i18n.language)}</td>
                    <td className="num" style={{ textAlign: 'start',
                      color: 'var(--ink-muted)', fontSize: '0.78rem' }}>
                      {r.voucher_no}</td>
                    <td>
                      {r.party_name ?? '—'}
                      {r.status === 'CANCELLED' && (
                        <Chip size="small" label={t('report.cancelled')}
                          sx={{ ml: 1, height: 16, fontSize: 9 }} />
                      )}
                    </td>
                    <td className="num" style={{ textAlign: 'start',
                      color: 'var(--ink-muted)', fontSize: '0.78rem' }}>
                      {r.party_tax_number ?? ''}</td>
                    <td className="num" style={{ color: amountColor(r.taxable) }}>
                      {fmtMoney(r.taxable)}</td>
                    <td className="num" style={{ color: amountColor(r.vat) }}>
                      {fmtMoney(r.vat)}</td>
                    <td className="num" style={{ color: amountColor(r.total) }}>
                      {fmtMoney(r.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>{t('report.total')}</td>
                  <td className="num">{fmtMoney(q.data!.taxable)}</td>
                  <td className="num">{fmtMoney(q.data!.vat)}</td>
                  <td className="num">{fmtMoney(q.data!.total)}</td>
                </tr>
              </tfoot>
            </table>
          </Box>
        )}

        {/* Said on screen as well as on paper: a user who sees a
            negative row needs to know it is a reversal, not an error. */}
        <Typography variant="caption"
          sx={{ display: 'block', px: 2, pb: 2, color: c.inkMuted }}>
          {t('report.registerNote')}
        </Typography>
        <ReportFooter />
      </ReportShell>

      <VoucherDrawer voucherId={open} onClose={() => setOpen(null)} />
    </>
  )
}


function RegisterCard({ row, onOpen }:
{ row: RegisterRow; onOpen: () => void }) {
  const { t, i18n } = useTranslation()
  return (
    <Box onClick={onOpen}
      sx={{ px: 2, py: 1.25, cursor: 'pointer',
            '&:hover': { bgcolor: c.primarySoft } }}>
      <Stack direction="row" justifyContent="space-between"
        alignItems="baseline" spacing={1}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, minWidth: 0 }} noWrap>
          {row.party_name ?? '—'}
        </Typography>
        <span className="num" style={{ fontWeight: 600,
          color: amountColor(row.total) }}>
          {fmtMoney(row.total)}
        </span>
      </Stack>
      <Stack direction="row" justifyContent="space-between"
        sx={{ mt: 0.25, color: c.inkMuted, fontSize: 11.5 }}>
        <span className="num">
          {gregorian(row.date, i18n.language)} · {row.voucher_no}
        </span>
        {/* The VAT figure stays visible on a phone: it is the reason
            someone opens this report on one. */}
        <span className="num">{t('report.vat')} {fmtMoney(row.vat)}</span>
      </Stack>
    </Box>
  )
}
